import { format } from 'date-fns';
import type { VacationBalance, AcquisitionPeriod } from '@/features/Vacations/models/vacation.types';
import { LEGACY_PERIODS_CUTOFF_DATE } from '@/features/Vacations/vacations.definitions';
import { countVacationDays } from './vacationPeriodTotals';
import type { VacationPeriod } from '@/features/Collaborators/Edit/Types';

const ENTITLED_DAYS_PER_PERIOD = 30;
const EXPIRING_WINDOW_DAYS = 90;

interface PreviewInput {
  vacationPeriods: VacationPeriod[];
  startDate: string;
}

interface VacationBalancePreviewResult {
  balance: VacationBalance;
  isPreview: boolean;
  canPreview: boolean;
}

function daysBetween(from: Date, to: Date): number {
  const msPerDay = 1000 * 60 * 60 * 24;
  return Math.floor((to.getTime() - from.getTime()) / msPerDay);
}

function parseIso(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;
  const [, y, m, d] = match;
  const date = new Date(Date.UTC(Number(y), Number(m) - 1, Number(d)));
  return !isNaN(date.getTime()) ? date : null;
}

function summarizeFormLegacy(
  periods: VacationPeriod[],
  todayIso: string,
  cutoffIso: string = LEGACY_PERIODS_CUTOFF_DATE
): { taken: number; scheduled: number } {
  let taken = 0;
  let scheduled = 0;

  const validPeriods = (periods || [])
    .filter((p) => p.startDate && p.endDate && p.startDate <= p.endDate)
    .filter((p) => p.startDate < cutoffIso);

  for (const period of validPeriods) {
    const days = countVacationDays(period.startDate, period.endDate);
    if (period.startDate <= todayIso) {
      taken += days;
    } else {
      scheduled += days;
    }
  }

  return { taken, scheduled };
}

export function previewVacationBalance(
  serverBalance: VacationBalance | null,
  input: PreviewInput,
  todayIso: string
): VacationBalancePreviewResult {
  if (!serverBalance || !serverBalance.eligible) {
    return {
      balance: serverBalance || ({} as VacationBalance),
      isPreview: false,
      canPreview: false,
    };
  }

  if (input.startDate !== serverBalance.admissionDate) {
    return {
      balance: serverBalance,
      isPreview: false,
      canPreview: false,
    };
  }

  const formLegacy = summarizeFormLegacy(
    input.vacationPeriods,
    todayIso
  );

  if (
    formLegacy.taken === serverBalance.legacyTakenDays &&
    formLegacy.scheduled === serverBalance.legacyScheduledDays
  ) {
    return {
      balance: serverBalance,
      isPreview: false,
      canPreview: true,
    };
  }

  const deltaTaken = formLegacy.taken - serverBalance.legacyTakenDays;
  const deltaScheduled = formLegacy.scheduled - serverBalance.legacyScheduledDays;

  const periods = serverBalance.periods as AcquisitionPeriod[];

  let remainingTaken = deltaTaken;
  let remainingScheduled = deltaScheduled;

  const previewPeriods: AcquisitionPeriod[] = periods.map((period) => {
    if (period.status === 'Accruing') {
      return period;
    }

    let newTakenDays = period.takenDays;
    let newScheduledDays = period.scheduledDays;

    if (remainingTaken !== 0) {
      const maxCanTake = ENTITLED_DAYS_PER_PERIOD - newTakenDays;
      if (remainingTaken > 0) {
        const toAdd = Math.min(remainingTaken, maxCanTake);
        newTakenDays += toAdd;
        remainingTaken -= toAdd;
      } else {
        const toRemove = Math.min(-remainingTaken, newTakenDays);
        newTakenDays -= toRemove;
        remainingTaken += toRemove;
      }
    }

    if (remainingScheduled !== 0) {
      const maxCanSchedule = ENTITLED_DAYS_PER_PERIOD - newTakenDays - period.soldDays - period.pendingDays - period.pendingSoldDays;
      if (remainingScheduled > 0) {
        const toAdd = Math.min(remainingScheduled, maxCanSchedule);
        newScheduledDays += toAdd;
        remainingScheduled -= toAdd;
      } else {
        const toRemove = Math.min(-remainingScheduled, newScheduledDays);
        newScheduledDays -= toRemove;
        remainingScheduled += toRemove;
      }
    }

    const newBalanceDays = Math.max(
      0,
      ENTITLED_DAYS_PER_PERIOD -
        newTakenDays -
        period.soldDays -
        newScheduledDays -
        period.pendingSoldDays -
        period.pendingDays
    );

    const limit = parseIso(period.concessiveDeadline);
    const legal = parseIso(period.legalDeadline);
    const today = parseIso(todayIso);

    let status: AcquisitionPeriod['status'];
    if (newBalanceDays === 0) {
      status = 'Closed';
    } else if (legal && today && daysBetween(today, legal) < 0) {
      status = 'Expired';
    } else if (limit && today && daysBetween(today, limit) < 0) {
      status = 'PastLimit';
    } else if (limit && today && daysBetween(today, limit) <= EXPIRING_WINDOW_DAYS) {
      status = 'Expiring';
    } else {
      status = 'Available';
    }

    return {
      ...period,
      takenDays: newTakenDays,
      scheduledDays: newScheduledDays,
      balanceDays: newBalanceDays,
      status,
    };
  });

  const completedPeriods = previewPeriods.filter((p) => p.status !== 'Accruing');
  const balanceDays = completedPeriods.reduce((sum, p) => sum + p.balanceDays, 0);
  const sumByStatus = (status: AcquisitionPeriod['status']) =>
    completedPeriods
      .filter((p) => p.status === status)
      .reduce((sum, p) => sum + p.balanceDays, 0);
  const expiredDays = sumByStatus('Expired');
  const pastLimitDays = sumByStatus('PastLimit');
  const availableDays = Math.max(0, balanceDays - expiredDays - pastLimitDays);
  const earliest = (field: 'concessiveDeadline' | 'legalDeadline') =>
    completedPeriods
      .filter((p) => p.balanceDays > 0)
      .map((p) => p[field])
      .sort()[0] ?? null;
  const nextDeadline = earliest('concessiveDeadline');
  const nextLegalDeadline = earliest('legalDeadline');

  const unallocatedDays = Math.max(0, remainingTaken + remainingScheduled);

  const previewBalance: VacationBalance = {
    ...serverBalance,
    periods: previewPeriods,
    balanceDays,
    expiredDays,
    pastLimitDays,
    availableDays,
    nextDeadline,
    nextLegalDeadline,
    scheduledDays: serverBalance.scheduledDays + deltaScheduled,
    legacyTakenDays: formLegacy.taken,
    legacyScheduledDays: formLegacy.scheduled,
    unallocatedDays,
  };

  return {
    balance: previewBalance,
    isPreview: true,
    canPreview: true,
  };
}
